// Profiles API service for frontend

import { API_BASE_URL } from '../config/settings';

export interface Profile {
  id: string;
  username: string;
  platform: 'instagram' | 'youtube' | 'tiktok' | 'linkedin';
  platformId?: string;
  brand?: string;
  tipo?: string;
  monitorStories: boolean;
  monitorReels: boolean;
  monitorPosts: boolean;
  monitorCarousel: boolean;
  monitorAds: boolean;
  monitorVlog: boolean;
  readDaily?: string;
  lastStoryRead?: string;
  gdriveFolder?: string;
  saveTab?: string;
  lastCursor?: string;
  firstCursor?: string;
  reelsCount: number;
  postsCount: number;
  carouselCount: number;
  storiesCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface ProfileListQuery {
  platform?: string;
  brand?: string;
  saveTab?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ProfileStats {
  total: number;
  byPlatform: Record<string, number>;
  byBrand: Record<string, number>;
  bySaveTab: Record<string, number>;
}

class ProfilesApi {
  private baseUrl = `${API_BASE_URL}/profiles`;

  async list(query: ProfileListQuery = {}): Promise<{ profiles: Profile[]; total: number }> {
    const params = new URLSearchParams();
    if (query.platform) params.set('platform', query.platform);
    if (query.brand) params.set('brand', query.brand);
    if (query.saveTab) params.set('saveTab', query.saveTab);
    if (query.search) params.set('search', query.search);
    if (query.limit) params.set('limit', String(query.limit));
    if (query.offset) params.set('offset', String(query.offset));

    const res = await fetch(`${this.baseUrl}?${params.toString()}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to list profiles');
    }

    return json.data;
  }

  async getStats(): Promise<ProfileStats> {
    const res = await fetch(`${this.baseUrl}/stats`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get stats');
    }

    return json.data;
  }

  async getBrands(): Promise<string[]> {
    const res = await fetch(`${this.baseUrl}/brands`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get brands');
    }

    return json.data.brands;
  }

  async getSaveTabs(): Promise<string[]> {
    const res = await fetch(`${this.baseUrl}/save-tabs`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get save tabs');
    }

    return json.data.saveTabs;
  }

  async get(id: string): Promise<Profile> {
    const res = await fetch(`${this.baseUrl}/${id}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Profile not found');
    }

    return json.data;
  }

  async create(data: {
    username: string;
    platform?: Profile['platform'];
    platformId?: string;
    brand?: string;
    tipo?: string;
    monitorStories?: boolean;
    monitorReels?: boolean;
    monitorPosts?: boolean;
    monitorCarousel?: boolean;
    monitorAds?: boolean;
    monitorVlog?: boolean;
    saveTab?: string;
    gdriveFolder?: string;
  }): Promise<Profile> {
    const res = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to create profile');
    }

    return json.data;
  }

  async update(id: string, data: Partial<Profile>): Promise<Profile> {
    const res = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to update profile');
    }

    return json.data;
  }

  async delete(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE'
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to delete profile');
    }
  }

  async bulkImport(profiles: Array<Partial<Profile>>): Promise<{ imported: number; skipped: number }> {
    const res = await fetch(`${this.baseUrl}/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profiles })
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to import profiles');
    }

    return json.data;
  }

  async export(format: 'json' | 'csv' = 'json'): Promise<Profile[] | string> {
    const res = await fetch(`${this.baseUrl}/export?format=${format}`);

    if (format === 'csv') {
      return res.text();
    }

    const json = await res.json();
    if (!json.success) {
      throw new Error(json.error || 'Failed to export profiles');
    }

    return json.data.profiles;
  }

  async syncProfile(id: string): Promise<{
    profileId: string;
    profileUsername: string;
    platform: string;
    success: boolean;
    syncedCount: number;
    skippedCount: number;
    errorCount: number;
    errors?: string[];
    duration: number;
  }> {
    const res = await fetch(`${this.baseUrl}/${id}/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to sync profile');
    }

    return json.data;
  }

  async syncAll(): Promise<{
    results: Array<{
      profileId: string;
      profileUsername: string;
      platform: string;
      success: boolean;
      syncedCount: number;
      errors?: string[];
    }>;
    summary: {
      total: number;
      success: number;
      synced: number;
    };
  }> {
    const res = await fetch(`${this.baseUrl}/sync-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to sync profiles');
    }

    return json.data;
  }

  async getSyncStatus(id: string): Promise<{
    profileId: string;
    status: 'idle' | 'syncing' | 'error' | 'completed';
    lastSync?: number;
    syncedCount: number;
    error?: string;
  } | null> {
    const res = await fetch(`${this.baseUrl}/${id}/sync-status`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get sync status');
    }

    return json.data;
  }
}

export const profilesApi = new ProfilesApi();
